import { Share2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { buildReferralRegisterUrl } from '@/lib/referral-link';

import { useSubscriptionsContext } from './use-subscriptions-context';

export const SubscriptionsReferPage = () => {
  const { t } = useTranslation('billing');
  const { referral } = useSubscriptionsContext();

  const shareUrl = useMemo(
    () => (referral ? buildReferralRegisterUrl(referral.referralCode) : ''),
    [referral],
  );

  const copyText = async (text: string, successKey: 'referral.copied' | 'referral.linkCopied') => {
    await navigator.clipboard.writeText(text);
    toast.success(t(successKey));
  };

  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('referral.title')}</CardTitle>
        <CardDescription>{t('referral.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {referral ? (
          <>
            <div className="rounded-xl border border-dashed border-brand/30 bg-brand/5 p-5">
              <p className="text-caption text-fg-secondary">{t('referral.codeLabel')}</p>
              <p className="mt-1 font-mono text-heading-sm text-fg">{referral.referralCode}</p>
            </div>

            <div className="space-y-2">
              <p className="text-caption text-fg-secondary">{t('referral.linkLabel')}</p>
              <Input readOnly value={shareUrl} onFocus={(event) => event.target.select()} />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-body-sm text-fg-secondary">
                {t('referral.stats', {
                  rewarded: referral.rewardedCount,
                  total: referral.referralCount,
                })}
              </p>
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void copyText(referral.referralCode, 'referral.copied')}
                >
                  {t('referral.copy')}
                </Button>
                <Button type="button" onClick={() => void copyText(shareUrl, 'referral.linkCopied')}>
                  {t('referral.copyLink')}
                </Button>
                {canNativeShare ? (
                  <Button
                    type="button"
                    variant="secondary"
                    leadingIcon={<Share2 className="h-4 w-4" />}
                    onClick={() =>
                      void navigator.share({
                        title: t('referral.shareTitle'),
                        text: t('referral.shareText'),
                        url: shareUrl,
                      })
                    }
                  >
                    {t('referral.shareLink')}
                  </Button>
                ) : null}
              </div>
            </div>
          </>
        ) : (
          <p className="text-body-sm text-fg-secondary">{t('referral.loading')}</p>
        )}
      </CardContent>
    </Card>
  );
};
